import { AlertCircle, AlertTriangle, Info } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/shared/lib/utils";

import type { MetricAlert } from "./types";

const ICONS = {
  info: Info,
  warning: AlertTriangle,
  critical: AlertCircle,
} as const;

const STYLES = {
  info: "border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-100",
  warning:
    "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100",
  critical:
    "border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100",
} as const;

export interface MetricAlertsListProps {
  alerts: MetricAlert[];
  className?: string;
}

/**
 * Lista compacta de alertas/insights del hub de métricas.
 */
export function MetricAlertsList({
  alerts,
  className,
}: MetricAlertsListProps): React.JSX.Element | null {
  if (alerts.length === 0) return null;

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Alertas e insights</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {alerts.map((alert) => {
          const Icon = ICONS[alert.severity];
          return (
            <div
              key={alert.id}
              className={cn(
                "flex gap-3 rounded-lg border px-3 py-2.5 text-sm",
                STYLES[alert.severity]
              )}
            >
              <Icon className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="min-w-0">
                <p className="font-medium">{alert.title}</p>
                <p className="opacity-90">{alert.description}</p>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
